/**
 * LEAD-360-CRM-SURFACE-01 — Owner Lead 360 record-detail surface.
 *
 * The owner-facing consumer of the PHI-safe Lead 360 read-model
 * (`@/dashboard/owner/lead-360-view`) over the owner endpoint
 * (`GET /api/owner/lead-360?clinicId=&limit=`). It renders each PHI-free
 * `Lead360Input` as a CRM-style record-detail card with an ACTIVITY TIMELINE
 * and INTEGRATION/LINKAGE status — not generic dashboard KPI cards.
 *
 * CORE PRODUCT RULE — activity stream vs telephony linkage are DIFFERENT signals:
 *   - `events[]` is the ACTIVITY stream ("what happened to this lead": a `called`
 *     event reads as "Called via phone").
 *   - `callLinked` is the Office24x7 telephony LINKAGE status ("is this lead
 *     joined to a calls-table record yet"). It is rendered as "Telephony linkage"
 *     and never as call activity. `not_available` reads as "Not linked yet",
 *     NEVER as "not called" / "Call: not available" — a lead can have a `called`
 *     activity event while telephony linkage is still un-wired.
 *
 * SAFETY / HONESTY — carried straight through from the view-model; this surface
 * adds no data, fetches no PHI, and invents nothing:
 *   - PHI-FREE: the contract carries NO patient name / phone / email / age /
 *     gender / fingerprint / contact id / recording / transcript; this surface
 *     renders only safe fields (opaque safeLeadId, clinic LABEL, source/UTM,
 *     timestamps, lifecycle statuses, sanitized event summaries, provenance,
 *     caveat). It never calls the contact timeline / contact vault or any PHI
 *     source.
 *   - NO REINTERPRETATION: every record is projected through `buildLead360View`.
 *     Lifecycle hops keep DISTINCT honest statuses — `not_available`, `unknown`,
 *     `no`, `yes`, `probable` are never collapsed into "No".
 *   - HONEST EVENT STREAM: `eventStreamStatus: 'available'` with zero events
 *     renders a wired-empty state ("Activity stream connected — no activity
 *     recorded yet"); a legacy/omitted-status empty stream stays "not wired".
 *     Neither is ever phrased as "no activity happened".
 *   - HONEST EMPTY STATE / FAULT-ISOLATED: a `[]` response renders a clear empty
 *     message; the self-fetching wrapper owns its loading/error state so a fetch
 *     failure never breaks sibling panels (mirrors CallActionWorkbenchPanel /
 *     LeakageSnapshotPanel).
 *   - SCOPE: owner only; ONE clinic at a time, chosen from the dashboard's
 *     existing clinic list (no invented id, no all-clinic listing).
 *
 * Styling reuses the existing dashboard.css design tokens (`clinic-table`,
 * `status-chip--*`, `growth-readiness-row`, `panel-caveat`, `badge`) plus a
 * scoped `.lead360-*` block appended to dashboard.css — no new design doctrine.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatTimestampIst } from '@/lib/datetime-ist';

import { statusChipLabel } from '@/dashboard/ui/status-canon';

import {
  buildLead360View,
  isLead360SectionVisible,
  type Lead360AppointmentCardView,
  type Lead360Event,
  type Lead360HopStatus,
  type Lead360Input,
  type Lead360View,
} from '@/dashboard/owner/lead-360-view';
import {
  PHONE_AVAILABILITY_COPY,
  PHONE_MASK,
} from '@/dashboard/owner/LeadContactReveal';
import {
  formatLeadRef,
  latestActivitySummaryShort,
  leadDetailHref,
  lifecycleStageLabel,
} from '@/dashboard/owner/leads-object-view';

const DEFAULT_TITLE = 'Lead 360';

// ─── Honest status vocabulary ────────────────────────────────────────

/**
 * Status-chip tone per hop status. `not_available` (off / grey) stays VISUALLY
 * distinct from a confirmed `no` (pending / amber) and from `unknown` (neutral),
 * so uncertainty / un-wired is never flattened into a confirmed negative. Reuses
 * the existing `.status-chip--*` modifiers — no new tokens.
 */
export const HOP_STATUS_CHIP: Readonly<Record<Lead360HopStatus, string>> = {
  yes: 'status-chip status-chip--ready',
  no: 'status-chip status-chip--pending',
  probable: 'status-chip status-chip--partial',
  unknown: 'status-chip status-chip--neutral',
  not_available: 'status-chip status-chip--off',
};

/** Capture-hop labels (lead captured / source attributed). */
const CAPTURE_STATUS_LABEL: Readonly<Record<Lead360HopStatus, string>> = {
  yes: 'Confirmed',
  no: 'Missing',
  probable: 'Probable',
  unknown: 'Unknown',
  not_available: 'Not available',
};

/**
 * Linkage-hop labels (telephony / walk-in / revenue). `not_available` →
 * "Not linked yet" (linkage could not be computed — un-wired integration,
 * missing match key, or a failed lookup), `no` → "Not linked" (the lookup
 * ran and found nothing). Neither means "not contacted" — linkage is a
 * join state, not activity.
 */
export const LINK_STATUS_LABEL: Readonly<Record<Lead360HopStatus, string>> = {
  yes: 'Linked',
  no: 'Not linked',
  probable: 'Probable match',
  unknown: 'Unknown',
  not_available: 'Not linked yet',
};

/** Lifecycle rail (#3) — the at-a-glance pipeline. callLinked is telephony LINKAGE. */
const RAIL_STEPS: ReadonlyArray<{
  key: 'leadCaptured' | 'sourceCaptured' | 'callLinked' | 'walkinLinked' | 'revenueLinked';
  label: string;
  vocab: Readonly<Record<Lead360HopStatus, string>>;
}> = [
  { key: 'leadCaptured', label: 'Lead captured', vocab: CAPTURE_STATUS_LABEL },
  { key: 'sourceCaptured', label: 'Source attributed', vocab: CAPTURE_STATUS_LABEL },
  { key: 'callLinked', label: 'Telephony linkage', vocab: LINK_STATUS_LABEL },
  { key: 'walkinLinked', label: 'Walk-in linkage', vocab: LINK_STATUS_LABEL },
  { key: 'revenueLinked', label: 'Revenue linkage', vocab: LINK_STATUS_LABEL },
];

/** Integration status (#5) — the explained linkage block, separate from activity. */
const INTEGRATION_ROWS: ReadonlyArray<{
  key: 'callLinked' | 'walkinLinked' | 'revenueLinked';
  label: string;
}> = [
  { key: 'callLinked', label: 'Telephony — Office24x7 call record' },
  { key: 'walkinLinked', label: 'Walk-in registration' },
  { key: 'revenueLinked', label: 'Revenue / billing' },
];

/**
 * Honest one-line explanation per linkage + status. The telephony copy
 * explicitly cross-references the activity stream so a `not_available` /
 * `no` linkage can never be read as "the lead was not called".
 */
function integrationExplain(
  key: 'callLinked' | 'walkinLinked' | 'revenueLinked',
  status: Lead360HopStatus,
): string {
  if (key === 'callLinked') {
    switch (status) {
      case 'yes':
        return 'Linked to an Office24x7 call record (vendor-confirmed).';
      case 'probable':
        return 'A probable Office24x7 call-record match on phone evidence — clearly distinct from a vendor-confirmed link.';
      case 'no':
        return 'The call lookup ran and found no linked Office24x7 record yet. This is a linkage state only — it is never evidence about call activity. See Activity above for what happened.';
      case 'not_available':
        return 'Telephony linkage could not be computed for this lead (no linkable phone reference, or the lookup was unavailable). This is a linkage state only — see Activity above for what happened.';
      default:
        return 'Telephony linkage is unknown for this lead. This is a linkage signal only — see Activity above for what happened.';
    }
  }
  if (key === 'walkinLinked') {
    switch (status) {
      case 'yes':
        return 'Matched to a walk-in registration.';
      case 'probable':
        return 'A probable walk-in match — not yet confirmed.';
      case 'no':
        return 'No walk-in is linked to this lead.';
      case 'not_available':
        return 'Walk-in linkage is not wired yet.';
      default:
        return 'Walk-in linkage is unknown for this lead.';
    }
  }
  switch (status) {
    case 'yes':
      return 'Linked to billed revenue.';
    case 'probable':
      return 'A probable revenue match — not yet confirmed.';
    case 'no':
      return 'No revenue is linked to this lead.';
    case 'not_available':
      return 'Revenue linkage is not available yet.';
    default:
      return 'Revenue linkage is unknown for this lead.';
  }
}

// ─── Activity-event presentation (PHI-free, deterministic) ────────────

/**
 * Operator-facing label per event type. Covers the `lead_events` DB enum
 * plus the read-time VIEW members (`Lead360ViewEventType` — e.g.
 * `call_logged`, a linked call whose outcome is not classifiable).
 * Unknown → humanized.
 */
const EVENT_TYPE_LABEL: Readonly<Record<string, string>> = {
  lead_created: 'Lead created',
  called: 'Called',
  // OMNICHANNEL-TIMELINE-AND-LEAD-360-V1: the composer's note entry.
  note_added: 'Note',
  appointment_booked: 'Appointment booked',
  // S2-P2D: appointment lifecycle activity (read-time, from appointment_events).
  // AGENT-TO-PATIENT-SPINE-V1: booking confirmed with the patient (SLA item 4).
  appointment_confirmed: 'Appointment confirmed',
  appointment_rescheduled: 'Appointment rescheduled',
  appointment_cancelled: 'Appointment cancelled',
  appointment_no_show: 'Appointment marked no-show',
  // S2-P3: check-in / outcome activity (read-time, from appointment_events).
  patient_arrived: 'Arrived',
  patient_checked_in: 'Checked in',
  appointment_fulfilled: 'Consultation fulfilled',
  lead_not_converted: 'Lead not converted',
  // S2-P5A4: walk-in registration activity (read-time, from walkins.lead_id).
  walkin_registered: 'Walk-in registered',
  visited: 'Visited',
  not_answered: 'Not answered',
  closed: 'Closed',
  missed_call: 'Missed call',
  follow_up_needed: 'Follow-up needed',
  call_logged: 'Call logged',
  // CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: read-time contact-reveal
  // activity (from audit_log CONTACT_REVEAL rows; PHI-free -- purpose token +
  // role only, never the revealed value).
  contact_revealed: 'Contact revealed',
};

export function eventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABEL[eventType] ?? eventType.replace(/_/g, ' ').trim();
}

/**
 * The controlled not-converted reasons (S2-P3) and their operator-facing
 * labels - the structural mirror of the backend OUTCOME_REASONS
 * (lib/domain/appointment/appointment-events.ts). Ordered for the reason
 * dropdown; the value is the stored token, the label is display-only. NEVER a
 * free-text note. Shared by the workbench reason picker + the Activity render.
 */
export const OUTCOME_REASON_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: 'price', label: 'Price' },
  { value: 'location', label: 'Location' },
  { value: 'timing', label: 'Timing' },
  { value: 'no_response', label: 'No response' },
  { value: 'chose_competitor', label: 'Chose competitor' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
];

const OUTCOME_REASON_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  OUTCOME_REASON_OPTIONS.map((o) => [o.value, o.label]),
);

/** Operator-facing label for a controlled outcome reason, else the raw token. */
export function outcomeReasonLabel(reason: string): string {
  return OUTCOME_REASON_LABEL[reason] ?? reason;
}

/**
 * DOCTOR-HOME-STATE-MODEL-V1 (F6a): formatTimestampIst now lives in the ONE
 * IST owner (frontend/src/lib/datetime-ist.ts) -- the local copy here was the
 * LES-126 second-formatter class (it read a zone-less timestamp as UTC and
 * double-shifted an already-IST value). Re-exported from this, its historic
 * home, so all existing consumers heal with zero call-site churn (the
 * day-scope re-export precedent).
 */
export { formatTimestampIst };

/** True only for a non-empty, non-whitespace string. Handles null/undefined. */
function isPresent(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

function titleCase(s: string): string {
  const t = s.trim();
  return t.length === 0 ? t : t.charAt(0).toUpperCase() + t.slice(1);
}

/** The single honest "no concrete source" sentinel returned by `compactSourceSummary`. */
export const SOURCE_NOT_CAPTURED = 'Source not captured';

/**
 * Compact, human source summary for the record header (e.g. "Google paid
 * search"). PHI-free — only the normalized source + UTM source. Honest when
 * absent (returns `SOURCE_NOT_CAPTURED`).
 */
export function compactSourceSummary(input: Lead360Input): string {
  const src = isPresent(input.sourceNormalized)
    ? input.sourceNormalized.trim().replace(/_/g, ' ')
    : '';
  // F10 (AUG-12-WALK-BLOCKERS-V1, enterprise copy law): only a KNOWN ad
  // platform earns the header slot ("Google paid search" stays exactly as
  // it was) -- an UNMAPPED utm_source is a machine token (the founder's
  // "partha-domain-check" leak) and never renders on the human summary; the
  // line falls back to the controlled normalized source.
  const utmRaw = isPresent(input.utmSource) ? input.utmSource.trim().toLowerCase() : '';
  const utm = utmRaw !== '' && AD_PLATFORM_LABELS[utmRaw] !== undefined ? titleCase(utmRaw) : '';
  if (utm && src) return `${utm} ${src}`;
  if (utm) return utm;
  if (src) return titleCase(src);
  return SOURCE_NOT_CAPTURED;
}

/**
 * True when a concrete marketing source summary is resolvable for this lead — i.e.
 * `compactSourceSummary` returns a real value like "Google paid search" rather
 * than `SOURCE_NOT_CAPTURED`. DERIVED from `compactSourceSummary`'s own output so
 * the two can never drift. Drives the source-coherence rule (a known source must
 * not co-render an "Unknown source" badge; a truly unknown-source lead still
 * does). PHI-free.
 */
export function hasResolvedSourceSummary(input: Lead360Input): boolean {
  return compactSourceSummary(input) !== SOURCE_NOT_CAPTURED;
}

// ─── Visual accents (ADORACARE-CRM-VISUAL-BENCHMARK-01) ───────────────

/**
 * Initials-style avatar text derived ONLY from the opaque safe lead ref
 * (e.g. '36' → 'L36'). Deterministic and PHI-free by construction — it never
 * sees a name, phone, or any patient field.
 */
export function leadRefInitials(safeLeadId: string): string {
  const compact = safeLeadId.replace(/[^a-zA-Z0-9]/g, '');
  if (compact.length === 0) return 'L';
  return `L${compact.slice(-2).toUpperCase()}`;
}

/**
 * Tiny inline SVG icon accents for the record sections — no icon library, no
 * new dependency. Decorative only (aria-hidden); fixed geometric paths.
 */
const SECTION_ICON_PATHS = {
  highlights: 'M8 2l1.6 4.4L14 8l-4.4 1.6L8 14l-1.6-4.4L2 8l4.4-1.6z',
  source:
    'M8 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm0 3.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z',
  journey: 'M3.5 13.5v-11h8.2l-2 2.7 2 2.7H3.5',
  activity: 'M1.5 8.5h3L6.5 4l3 8 1.7-3.5h3.3',
  integration:
    'M6.7 9.3l2.6-2.6M5.4 6.9 4 8.3a2.4 2.4 0 0 0 3.4 3.4l1.4-1.4M10.6 9.1 12 7.7a2.4 2.4 0 0 0-3.4-3.4L7.2 5.7',
} as const;

export type SectionIconName = keyof typeof SECTION_ICON_PATHS;

export function SectionIcon({ name }: { name: SectionIconName }): JSX.Element {
  return (
    <svg
      className="lead360-section__icon"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={SECTION_ICON_PATHS[name]} />
    </svg>
  );
}

// ─── Record sections ─────────────────────────────────────────────────

/**
 * Lifecycle-stage chip tone (LEADS-WORKSPACE-ROLE-SCOPED-01 header
 * status). A STAGE is progress, not a confirmed-missing hop, so it
 * never uses the amber "pending/missing" tone: complete → ready,
 * in-progress stages → partial, unknown → neutral.
 */
export const STAGE_CHIP_CLASS: Readonly<
  Record<Lead360View['lifecycleCompleteness'], string>
> = {
  complete: 'status-chip status-chip--ready',
  lead_plus_walkin: 'status-chip status-chip--partial',
  partial: 'status-chip status-chip--partial',
  lead_only: 'status-chip status-chip--partial',
  unknown: 'status-chip status-chip--neutral',
};

function RecordHeader({
  view,
  compactSource,
  sourceResolved,
  showStageChip = false,
}: {
  view: Lead360View;
  compactSource: string;
  sourceResolved: boolean;
  /**
   * Flag-gated header STATUS (LEADS-WORKSPACE-ROLE-SCOPED-01: name,
   * clinic, source, status). FALSE renders today's header byte-
   * identically (owner dashboard panel and flag-off detail).
   */
  showStageChip?: boolean;
}): JSX.Element {
  const dataModeClass =
    view.dataMode === 'real' ? 'badge badge--success' : 'badge badge--warning';
  // Source-coherence rule (LEADS-OBJECT-CRM-SHELL-01 review): suppress the
  // contradictory "Unknown source" data-mode badge ONLY when a concrete source
  // summary (e.g. "Google paid search") is already resolved, so the header never
  // shows both at once. Every other data mode still renders, and a truly
  // unknown-source lead still shows "Unknown source" — real uncertainty is not
  // hidden. The pure view-model is unchanged (it still maps unknown ->
  // "Unknown source"); this is a presentation-only coherence guard that applies
  // wherever the record renders (Lead detail AND the Owner dashboard panel).
  const showDataModeBadge = !(view.dataMode === 'unknown' && sourceResolved);
  // Role-aware: the compact source summary belongs to the 'source' section, so
  // it is suppressed for tiers that do not receive source/attribution (e.g.
  // front-desk). Backward-compatible — undefined manifest ⇒ shown.
  const showSourceSummary = isLead360SectionVisible(view.visibleSections, 'source');
  return (
    <header className="lead360-record__header">
      {/*
        Initials-style avatar derived from the SAFE lead ref only (no names,
        no PHI) — decorative, so hidden from assistive tech (the title below
        already reads "Lead #<ref>").
      */}
      <span
        className="lead360-avatar"
        aria-hidden="true"
        data-testid="lead360-avatar"
      >
        {leadRefInitials(view.safeLeadId)}
      </span>
      <div className="lead360-record__heading">
        <span className="lead360-record__eyebrow">Lead record</span>
        {/*
          CONTACT-REVEAL-AUDIT-01 (decision 3): the lead name renders PLAIN
          as the record title for scoped operators, with the safe ref kept
          beneath it as the stable reference. When no name is captured the
          original ref-as-title markup renders unchanged — a name is never
          fabricated.
        */}
        {view.leadName !== null ? (
          <>
            <h3 className="lead360-record__title" data-testid="lead360-leadname">
              {view.leadName}
            </h3>
            <span className="lead360-record__subref" data-testid="lead360-leadref">
              Lead #{view.safeLeadId}
            </span>
          </>
        ) : (
          <h3 className="lead360-record__title" data-testid="lead360-leadref">
            Lead #{view.safeLeadId}
          </h3>
        )}
        <p className="lead360-record__meta">
          <span className="lead360-record__clinic">{view.clinicLabel}</span>
          {showSourceSummary ? (
            <>
              <span aria-hidden="true"> · </span>
              <span data-testid="lead360-source-summary">{compactSource}</span>
            </>
          ) : null}
        </p>
      </div>
      <div className="lead360-record__headmeta">
        {showStageChip ? (
          <span
            className={STAGE_CHIP_CLASS[view.lifecycleCompleteness]}
            data-testid="lead360-stage-chip"
          >
            {lifecycleStageLabel(view)}
          </span>
        ) : null}
        {showDataModeBadge ? (
          <span className={dataModeClass} data-testid="lead360-datamode">
            {view.dataModeLabel}
          </span>
        ) : null}
        <span className="lead360-record__captured" data-testid="lead360-captured">
          Captured {formatTimestampIst(view.createdAtDisplay)}
        </span>
      </div>
    </header>
  );
}

/**
 * Contact section (CONTACT-REVEAL-AUDIT-01). Phone renders as a ZERO-DIGIT
 * presence mask plus a digit-free availability status (decision 4) — never a
 * value. On the Lead 360 DETAIL page the caller passes `revealControl` (the
 * audited tap-to-reveal control) and it renders INSTEAD of the static row;
 * everywhere else (dashboard panel cards) only the static masked row shows —
 * no list-level or panel-level reveal exists in v1.
 */
export function ContactSection({
  view,
  revealControl,
}: {
  view: Lead360View;
  revealControl?: ReactNode;
}): JSX.Element {
  return (
    <section className="lead360-section" aria-label="Contact">
      <h4 className="lead360-section__title">
        <SectionIcon name="source" />
        Contact
      </h4>
      <div className="lead360-kv" data-testid="lead360-contact">
        <div className="lead360-kv__row">
          <dt className="lead360-kv__key">Phone</dt>
          <dd className="lead360-kv__val">
            {revealControl ?? (
              <div className="lead360-contact__row" data-testid="lead360-phone-row">
                <span
                  className="lead360-contact__mask"
                  data-testid="lead360-phone-mask"
                >
                  {PHONE_MASK}
                </span>
                <span
                  className="lead360-muted"
                  data-testid="lead360-phone-availability"
                  data-availability={view.phoneAvailability}
                >
                  {PHONE_AVAILABILITY_COPY[view.phoneAvailability]}
                </span>
              </div>
            )}
          </dd>
        </div>
      </div>
    </section>
  );
}

/** Title-case a raw source/UTM token: 'paid_search' -> 'Paid search'. Pure. */
export function humanizeSourceToken(token: string | null | undefined): string {
  const t = (token ?? '').trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (t === '') return '';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

/** Known ad-platform display names for the Channel row (utm_source -> platform). */
const AD_PLATFORM_LABELS: Readonly<Record<string, string>> = {
  google: 'Google Ads',
  google_ads: 'Google Ads',
  adwords: 'Google Ads',
  facebook: 'Meta Ads',
  fb: 'Meta Ads',
  meta: 'Meta Ads',
  instagram: 'Instagram',
  ig: 'Instagram',
  bing: 'Microsoft Ads',
  microsoft: 'Microsoft Ads',
  youtube: 'YouTube',
  linkedin: 'LinkedIn Ads',
  twitter: 'X Ads',
  x: 'X Ads',
};

/** Known marketing-medium display names for the Medium row. */
const MEDIUM_LABELS: Readonly<Record<string, string>> = {
  cpc: 'Paid search',
  ppc: 'Paid search',
  paid_search: 'Paid search',
  paidsearch: 'Paid search',
  paid: 'Paid',
  paid_social: 'Paid social',
  social: 'Social',
  organic: 'Organic',
  organic_search: 'Organic search',
  email: 'Email',
  referral: 'Referral',
  display: 'Display',
  affiliate: 'Affiliate',
  direct: 'Direct',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

/**
 * THE CHANNEL-LABEL FOLD (LEAD-360-CONFORMANCE-B1 gap G7, closed by
 * LEAD-360-CONFORMANCE-B2 G3/G1 correctives): named capture-channel doors
 * get a fixed, human label instead of the generic de-slugger -- a bare
 * `walk_in` must read "Walk-in" (hyphenated, matching the founder's own
 * worked example), never "Walk in". Extend this map, never invent a
 * second one, when a new channel token needs a fixed label. */
const CHANNEL_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  whatsapp: 'WhatsApp',
  webchat: 'Web chat',
  web_chat: 'Web chat',
  walk_in: 'Walk-in',
  phone: 'Phone call',
  phone_call: 'Phone call',
  talk_to_chat: 'Talk to Chat',
  website_form: 'Website form',
};

/** The human CHANNEL label: the ad platform from utm_source when KNOWN, else
 *  the CONTROLLED normalized source, folded through CHANNEL_LABEL_OVERRIDES
 *  when it names a known capture door, else title-cased. AUG-12-WALK-BLOCKERS-V1
 *  (F10, enterprise copy law): an UNMAPPED utm_source is a machine token
 *  (the founder's "partha-domain-check" leak) -- de-snaking it still prints
 *  operator plumbing on a human record, so it never renders here; the
 *  channel line falls back to the controlled source vocabulary and the raw
 *  utm value stays available on the attribution evidence surfaces. */
export function sourceChannelLabel(
  sourceNormalized: string | null | undefined,
  utmSource: string | null | undefined,
): string {
  const src = (utmSource ?? '').trim().toLowerCase();
  if (src !== '') {
    const mapped = AD_PLATFORM_LABELS[src];
    if (mapped !== undefined) return mapped;
  }
  const norm = (sourceNormalized ?? '').trim().toLowerCase();
  const overridden = CHANNEL_LABEL_OVERRIDES[norm];
  if (overridden !== undefined) return overridden;
  return humanizeSourceToken(sourceNormalized);
}

/**
 * ORIGIN-CHIP SEMANTICS (founder ruling 2026-08-08, product-wide, banked in
 * docs/design/design-language/07-BEHAVIOURAL-LAWS.md -- LEAD-360-CONFORMANCE-B2
 * G3): the masthead origin chip shows the most meaningful KNOWN attribution,
 * in this order: CAMPAIGN -> SOURCE -> CHANNEL. A recorded campaign (human-
 * authored or humanized) always wins; absent that, a recognized ad-platform
 * SOURCE (sourceChannelLabel's ad-platform branch); absent that, the honest
 * capture CHANNEL/door (WhatsApp / Phone call / Walk-in / Website form /
 * Talk to Chat / ...). NEVER infers a deeper source than what is recorded --
 * a bare capture channel is the honest floor, not a guess. Derived from
 * EXISTING attribution fields only; no new writer, no new wire field. The
 * full attribution chain (Channel / Medium / Campaign / Paid click) stays
 * verbatim in the Campaign and source rail section -- this compacts ONE
 * chip, it never re-derives or replaces that section. */
export function resolveOriginChipLabel(input: {
  readonly sourceNormalized: string | null | undefined;
  readonly utmSource: string | null | undefined;
  readonly utmCampaign: string | null | undefined;
}): string {
  if (isPresent(input.utmCampaign)) {
    return humanizeSourceToken(input.utmCampaign);
  }
  return sourceChannelLabel(input.sourceNormalized, input.utmSource);
}

/** The human MEDIUM label for the Medium row: the mapped medium from utm_medium
 *  when known, else a title-cased utm_medium. '' when no utm_medium (so the row
 *  is shown ONLY when the lead actually carries a medium - no fabrication). */
export function sourceMediumLabel(
  utmMedium: string | null | undefined,
): string {
  const med = (utmMedium ?? '').trim().toLowerCase();
  if (med === '') return '';
  return MEDIUM_LABELS[med] ?? humanizeSourceToken(med);
}

/**
 * Source / UTM section. LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 6 / E5a):
 * humanized labels only - no raw machine tokens (paid_search / google / cpc) on
 * the human record. The Channel row folds utm_source into an ad-platform name
 * ("Google Ads"); Medium reads a human medium ("Paid search"); Campaign / Term /
 * Content stay as their (human-authored) verbatim values under clear labels. The
 * paid-click indicator below is PRESENCE-ONLY (Yes/No) - never a raw click id.
 */
export function SourceSection({ input }: { input: Lead360Input }): JSX.Element {
  const rows: Array<{ label: string; value: string }> = [];
  const channel = sourceChannelLabel(input.sourceNormalized, input.utmSource);
  if (channel !== '') {
    rows.push({ label: 'Channel', value: channel });
  }
  const medium = sourceMediumLabel(input.utmMedium);
  if (medium !== '') {
    rows.push({ label: 'Medium', value: medium });
  }
  if (isPresent(input.utmCampaign)) {
    rows.push({ label: 'Campaign', value: input.utmCampaign.trim() });
  }
  if (isPresent(input.utmTerm)) {
    rows.push({ label: 'Term', value: input.utmTerm.trim() });
  }
  if (isPresent(input.utmContent)) {
    rows.push({ label: 'Content', value: input.utmContent.trim() });
  }
  // Presence-only paid-click indicator (Yes/No). Always shown — "No" is an
  // honest, meaningful state — and never carries a raw click-id value.
  const paidClick = input.paidClickPresent === true;
  return (
    <section className="lead360-section" aria-label="Source">
      <h4 className="lead360-section__title">
        <SectionIcon name="source" />
        Source
      </h4>
      {rows.length > 0 ? (
        <dl className="lead360-kv" data-testid="lead360-source">
          {rows.map((r) => (
            <div className="lead360-kv__row" key={r.label}>
              <dt className="lead360-kv__key">{r.label}</dt>
              <dd className="lead360-kv__val">{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="lead360-muted" data-testid="lead360-source-empty">
          Source not captured.
        </p>
      )}
      <dl className="lead360-kv" data-testid="lead360-paid-click">
        <div className="lead360-kv__row">
          <dt className="lead360-kv__key">Paid click</dt>
          <dd
            className="lead360-kv__val"
            data-paid-click={paidClick ? 'yes' : 'no'}
          >
            {paidClick ? 'Yes' : 'No'}
          </dd>
        </div>
      </dl>
    </section>
  );
}

/** Journey rail (#3): five lifecycle steps as distinct, honest status chips. */
export function JourneyRail({ input }: { input: Lead360Input }): JSX.Element {
  return (
    <section className="lead360-section" aria-label="Lead journey">
      <h4 className="lead360-section__title">
        <SectionIcon name="journey" />
        Journey
      </h4>
      <ol className="lead360-rail" data-testid="lead360-journey">
        {RAIL_STEPS.map((step) => {
          const status = input[step.key];
          return (
            <li
              className="lead360-rail__step"
              key={step.key}
              data-hop={step.key}
              data-hop-status={status}
            >
              <span className="lead360-rail__label">{step.label}</span>
              <span className={HOP_STATUS_CHIP[status]}>{step.vocab[status]}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function EventRow({ event }: { event: Lead360Event }): JSX.Element {
  const when = formatTimestampIst(event.occurredAt);
  // S2-P2D: a reschedule activity entry carries the old/new appointment times so
  // we can show the change in IST. ASCII separator " -> " only (no en/em dash).
  const showReschedule =
    event.eventType === 'appointment_rescheduled' &&
    isPresent(event.previousAppointmentTime) &&
    isPresent(event.newAppointmentTime);
  // S2-P3: a not-converted entry shows its controlled reason inline with the
  // type label, e.g. "Lead not converted (Price)".
  const reasonLabel =
    event.eventType === 'lead_not_converted' && isPresent(event.outcomeReason)
      ? outcomeReasonLabel(event.outcomeReason as string)
      : null;
  return (
    <li
      className="lead360-event"
      data-testid="lead360-event"
      data-event-type={event.eventType}
      // LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 6 / E5b): the raw read-model
      // provenance is no longer printed as a "via *_read_model_v0" line on the
      // human feed. It is preserved as a hover tooltip (+ data attribute) so the
      // technical source is still one hover away, never visible clutter.
      data-provenance={isPresent(event.provenance) ? event.provenance : undefined}
      title={isPresent(event.provenance) ? `Read model: ${event.provenance}` : undefined}
    >
      <span className="lead360-event__marker" aria-hidden="true" />
      <div className="lead360-event__body">
        <div className="lead360-event__head">
          <span className="lead360-event__type">
            {eventTypeLabel(event.eventType)}
            {reasonLabel !== null ? ` (${reasonLabel})` : ''}
          </span>
          {when ? <span className="lead360-event__when">{when}</span> : null}
        </div>
        <p className="lead360-event__summary">{event.summary}</p>
        {showReschedule ? (
          <p className="lead360-muted" data-testid="lead360-event-reschedule">
            {`${formatTimestampIst(event.previousAppointmentTime as string)} -> ${formatTimestampIst(
              event.newAppointmentTime as string,
            )}`}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Activity stream (#4): the events[] history. Honest empty states keep the
 * wired-but-empty case ("connected — no activity recorded yet") DISTINCT from a
 * legacy un-wired stream ("not wired yet"); neither says "no activity happened".
 */
function ActivitySection({ view }: { view: Lead360View }): JSX.Element {
  const { events, eventStreamStatus } = view;
  return (
    <section className="lead360-section" aria-label="Activity">
      <h4 className="lead360-section__title">
        <SectionIcon name="activity" />
        Activity
      </h4>
      {events.length > 0 ? (
        <ol className="lead360-timeline" data-testid="lead360-activity">
          {events.map((ev, i) => (
            <EventRow key={`${ev.eventType}:${ev.occurredAt}:${i}`} event={ev} />
          ))}
        </ol>
      ) : eventStreamStatus === 'available' ? (
        <p
          className="lead360-muted"
          data-testid="lead360-activity-empty-wired"
          data-event-stream-status="available"
        >
          Activity stream connected — no activity recorded yet.
        </p>
      ) : (
        <p
          className="lead360-muted"
          data-testid="lead360-activity-not-wired"
          data-event-stream-status="not_wired"
        >
          Activity stream not wired yet.
        </p>
      )}
    </section>
  );
}

/**
 * Operator-facing label per canonical appointment status. A stored status that
 * did not project onto the canonical view (`status === null`) is shown humanized
 * from `storedStatus` — never guessed into a canonical bucket.
 */
export function appointmentStatusLabel(card: Lead360AppointmentCardView): string {
  // AGENT-TO-PATIENT-SPINE-V1: surface the retained 'confirmed' behavioral
  // signal. It projects onto the canonical view as 'booked', but the operator
  // must SEE that the appointment was confirmed with the patient (SLA item 4) --
  // so the stored signal wins for the display label here.
  // RECEPTION-OS-BUILD-V1 (F-09): the panel's local label map died; labels
  // resolve through THE ONE StatusChip canon (mechanical import update).
  if (card.storedStatus === 'confirmed') {
    return statusChipLabel('appointment', 'confirmed');
  }
  if (card.status !== null) return statusChipLabel('appointment', card.status);
  const humanized = card.storedStatus.replace(/_/g, ' ').trim();
  return humanized.length > 0 ? humanized : 'Unknown';
}

/**
 * Appointment section (S2-P2): the lead's REAL `appointments` rows as
 * administrative cards — date/time (IST), status, doctor-assigned yes/no, and
 * category when real. PHI-FREE by construction: the contract carries no name /
 * phone / clinical note / doctor identity, so none can render here. Honest
 * empty-state ("No appointments yet") when the lead has none — never a fixture
 * card. Renders NOTHING when the producer did not wire the section
 * (`appointmentSection` undefined) — fully backward-compatible. OPERATIONAL:
 * the front desk books and manages appointments, so this section is visible to
 * every operator tier (the LeadCard gate uses the role manifest, where
 * 'appointment' is present for all tiers including front_desk).
 */
function AppointmentSection({ input }: { input: Lead360Input }): JSX.Element | null {
  const section = input.appointmentSection;
  if (section === undefined) return null;
  return (
    <section className="lead360-section" aria-label="Appointments">
      <h4 className="lead360-section__title">
        <SectionIcon name="journey" />
        Appointments
      </h4>
      {section.hasAppointments ? (
        <ol className="lead360-appointments" data-testid="lead360-appointments">
          {section.cards.map((card) => (
            <li
              className="lead360-appointment"
              key={card.appointmentId}
              data-testid="lead360-appointment"
              data-appointment-status={card.storedStatus}
            >
              <div className="lead360-appointment__head">
                <span className="lead360-appointment__when">
                  {formatTimestampIst(card.appointmentTime)}
                </span>
                <span className="status-chip status-chip--neutral">
                  {appointmentStatusLabel(card)}
                </span>
              </div>
              <p className="lead360-appointment__meta">
                <span data-testid="lead360-appointment-doctor">
                  {card.doctorAssigned ? 'Doctor assigned' : 'No doctor assigned'}
                </span>
                {isPresent(card.category) ? (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span data-testid="lead360-appointment-category">
                      {card.category}
                    </span>
                  </>
                ) : null}
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <p className="lead360-muted" data-testid="lead360-appointments-empty">
          {section.emptyStateLabel ?? 'No appointments yet'}
        </p>
      )}
    </section>
  );
}

/**
 * Integration status (#5): telephony / walk-in / revenue LINKAGE as honest
 * data-status rows, explicitly separate from the activity stream and framed as
 * wiring (never as failures). Reuses `.growth-readiness-row`.
 */
export function IntegrationSection({ input }: { input: Lead360Input }): JSX.Element {
  return (
    <section className="lead360-section" aria-label="Integration status">
      <h4 className="lead360-section__title">
        <SectionIcon name="integration" />
        Integration &amp; linkage status
      </h4>
      <p className="lead360-muted lead360-section__intro">
        Whether this lead is joined to other systems. This is linkage wiring —
        separate from the activity above, and not a failure.
      </p>
      <div data-testid="lead360-integration">
        {INTEGRATION_ROWS.map((row) => {
          const status = input[row.key];
          return (
            <div
              className="growth-readiness-row"
              key={row.key}
              data-link={row.key}
              data-link-status={status}
            >
              <div className="growth-readiness-row__main">
                <span className="growth-readiness-row__label">{row.label}</span>
                <p className="growth-readiness-row__explain">
                  {integrationExplain(row.key, status)}
                </p>
              </div>
              <span className={HOP_STATUS_CHIP[status]}>{LINK_STATUS_LABEL[status]}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Concise, stable record-level data note (VISUAL-POLISH-01). The PRIMARY honesty
 * line on the record, short enough for the demo page, while the verbatim per-lead
 * caveat + provenance move into the lower-emphasis "Read model details" disclosure
 * below. States every honesty concept the record must keep visible: PHI-free read
 * model, the activity-vs-linkage separation, the Office24x7 / walk-in / revenue
 * integration gaps (shown, not hidden), and that nothing is inferred.
 */
export const LEAD_RECORD_DATA_NOTE =
  'PHI-free Lead 360 read model. Activity (what happened to the lead) is shown ' +
  'separately from Office24x7, walk-in, and revenue linkage; integration gaps are ' +
  'shown honestly per lead, never inferred.';

/**
 * Accurate-security replacement for the stale "PHI-free" wording
 * (LEADS-WORKSPACE-ROLE-SCOPED-01 inherited item): since the
 * CONTACT-REVEAL-AUDIT-01 carve-out this record shows the lead's name
 * to scoped staff and hosts the audited phone reveal, so "PHI-free" is
 * no longer true of the surface. Rendered ONLY when the caller passes
 * `polished` (flag-gated); the V1 string above stays byte-identical
 * for the flag-off render.
 */
export const LEAD_RECORD_DATA_NOTE_V2 =
  'Scoped lead record: the lead name is shown to staff with verified ' +
  'clinic access, and the phone is available only through the audited ' +
  'reveal (every reveal is logged). Activity (what happened to the lead) ' +
  'is shown separately from Office24x7, walk-in, and revenue linkage; ' +
  'integration gaps are shown honestly per lead, never inferred.';

/**
 * Data honesty (#6): a CONCISE standard "Data note" (LEAD_RECORD_DATA_NOTE) as the
 * primary, low-emphasis honesty line, with the verbatim per-lead caveat + provenance
 * tucked into a collapsed, lower-emphasis "Read model details" disclosure, so the
 * demo record page stays readable while every gap stays visible (nothing hidden,
 * nothing deleted).
 */
export function DataHonesty({
  view,
  polished = false,
  ownershipPlaneRow,
}: {
  view: Lead360View;
  /** Flag-gated accurate-copy variant; FALSE keeps today's note verbatim. */
  polished?: boolean;
  /**
   * ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1 (the owner-lens scope valve):
   * the finding-4 ownership-plane resolution REUSED (computed by the caller
   * via leads/lead-record-experience.ts resolveOwnershipPlaneRow -- typed
   * import only here, the rule is never relocated or re-derived).
   *   - undefined (legacy callers): today's raw assignedRoleLabel segment
   *     renders BYTE-IDENTICALLY;
   *   - null: the ladder guess is suppressed (personally assigned lead --
   *     the Owner row is the accountable plane);
   *   - a row: renders plane-labeled ("Next action owner" / "Responsible
   *     team"), never a stale owner claim.
   */
  ownershipPlaneRow?: import('@/dashboard/leads/lead-record-experience').OwnershipPlaneRow | null;
}): JSX.Element {
  return (
    <footer className="lead360-record__footer">
      <details className="lead360-record__readmodel" data-testid="lead360-readmodel">
        <summary className="lead360-record__readmodel-summary">
          Read model details
        </summary>
        {/* PROSE SWEEP (LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1 Fix 5):
            the always-visible "Data note" paragraph demotes INSIDE the
            existing collapsed disclosure -- the honesty copy is unchanged
            and one click away; the working surface below the record stays
            labels/values/chips only. Same testid, same strings. */}
        <p
          className="lead360-record__provenance lead360-record__datanote"
          role="note"
          data-testid="lead360-caveat"
        >
          Data note: {polished ? LEAD_RECORD_DATA_NOTE_V2 : LEAD_RECORD_DATA_NOTE}
        </p>
        <p
          className="lead360-record__provenance lead360-record__readmodel-line"
          data-testid="lead360-caveat-full"
        >
          {view.caveat}
        </p>
        <p
          className="lead360-record__provenance lead360-record__readmodel-line"
          data-testid="lead360-provenance"
        >
          {/*
            LIFECYCLE-RENDER-V1 (V1-P2): this value is `lifecycleCompleteness`
            (LINKAGE/identity completeness), so it is labelled "Linkage" - NOT
            "Lifecycle". The canon lifecycle STAGE lives in the stage rail; never
            reprint linkage completeness under a "lifecycle" label.
          */}
          Source system: {view.provenance.system} · Linkage:{' '}
          {view.lifecycleCompleteness} · Next: {view.nextActionLabel}
          {/*
            "Owns next action" is the management-routing OWNER — gated to the
            tiers that receive the 'nextActionOwner' section (leadership). For
            manager / front-desk it is omitted (the next-action LABEL above
            stays, derived from the always-visible journey hops).
          */}
          {isLead360SectionVisible(view.visibleSections, 'nextActionOwner')
            ? ownershipPlaneRow === undefined
              ? ` \u00B7 Owns next action: ${view.assignedRoleLabel}`
              : ownershipPlaneRow === null
                ? null
                : ` \u00B7 ${ownershipPlaneRow.label}: ${ownershipPlaneRow.value}`
            : null}
        </p>
      </details>
    </footer>
  );
}

/**
 * One lead record card. Projects the PHI-safe input through `buildLead360View`
 * (this component reinterprets nothing) and lays it out as a CRM record detail:
 * header → [optional highlights] → source → journey → activity → integration
 * status → data honesty. `highlights` is an optional slot rendered immediately
 * after the canonical record header — used by the Lead detail page so it does NOT
 * wrap its OWN duplicate "Lead record" header around this card. Omitted by the
 * dashboard / list (`Lead360Content`), so their rendering is unchanged. Exported
 * so the single-record detail page can compose it directly (one header, no dup).
 */
export function LeadCard({
  input,
  highlights,
  revealControl,
  appointmentControl,
  polished = false,
  showStageChip,
}: {
  input: Lead360Input;
  highlights?: ReactNode;
  /**
   * LIFECYCLE-RENDER-V1 (V1-P2): explicit override for the header
   * linkage-completeness chip, DECOUPLED from `polished`. Defaults to `polished`
   * (byte-identical for every existing caller). The canonical Lead 360 detail
   * page passes `false`: it now renders the canon lifecycle STAGE via the
   * dedicated stage rail, so the header no longer shows the linkage-completeness
   * word ("Source attributed") as if it were a lifecycle stage (the founder-
   * evidenced mixup). The linkage completeness still renders in the Journey rail
   * below, correctly labelled as linkage.
   */
  showStageChip?: boolean;
  /**
   * Optional audited tap-to-reveal control (CONTACT-REVEAL-AUDIT-01).
   * Passed ONLY by the Lead 360 detail page; everywhere else the Contact
   * section renders the static zero-digit presence mask.
   */
  revealControl?: ReactNode;
  /**
   * Optional appointment workbench control (S2-P2C). Passed ONLY by the Lead
   * 360 DETAIL page for an allowed role + enabled flag; rendered right after
   * the read-only appointment section. Everywhere else (owner panel, list) it
   * is absent and this card renders byte-identically.
   */
  appointmentControl?: ReactNode;
  /**
   * LEADS-WORKSPACE-ROLE-SCOPED-01, flag-gated by the caller
   * (me.leadsWorkspaceEnabled / the workspace pages): upgrades the
   * record header with the lifecycle STATUS chip (header = name,
   * clinic, source, status) and swaps the stale "PHI-free" data note
   * for the accurate-security copy. Default FALSE renders today's card
   * byte-identically (owner dashboard panel stays unchanged).
   */
  polished?: boolean;
}): JSX.Element {
  const view = buildLead360View(input);
  const compactSource = compactSourceSummary(input);
  // Role-aware section gating (S1-P4-ROLE-AWARE-LEAD360-VIEWS-01). The server
  // already stripped restricted-section field values; this renders ONLY the
  // sections the payload carries, so a hidden section is truly absent rather
  // than an empty "Not captured" card. Undefined manifest ⇒ everything shows.
  const sections = view.visibleSections;
  return (
    <article className="lead360-record" role="listitem" data-testid="lead360-card">
      <RecordHeader
        view={view}
        compactSource={compactSource}
        sourceResolved={hasResolvedSourceSummary(input)}
        showStageChip={showStageChip ?? polished}
      />
      {highlights ?? null}
      {isLead360SectionVisible(sections, 'contact') ? (
        <ContactSection view={view} revealControl={revealControl} />
      ) : null}
      {isLead360SectionVisible(sections, 'source') ? (
        <SourceSection input={input} />
      ) : null}
      {isLead360SectionVisible(sections, 'journey') ? (
        <JourneyRail input={input} />
      ) : null}
      {isLead360SectionVisible(sections, 'activity') ? (
        <ActivitySection view={view} />
      ) : null}
      {isLead360SectionVisible(sections, 'appointment') ? (
        <>
          <AppointmentSection input={input} />
          {appointmentControl ?? null}
        </>
      ) : null}
      {isLead360SectionVisible(sections, 'linkage') ? (
        <IntegrationSection input={input} />
      ) : null}
      <DataHonesty view={view} polished={polished} />
    </article>
  );
}

export interface Lead360ContentProps {
  records: readonly Lead360Input[];
}

const EMPTY_LABEL =
  'No Lead 360 records available for this clinic yet. Capture and source ' +
  'attribution data may still be pending.';

/**
 * Pure presentational body (no fetch, no hooks) — rendered directly in tests via
 * react-dom/server. Renders the record cards, or the honest empty state.
 */
export function Lead360Content({ records }: Lead360ContentProps): JSX.Element {
  if (records.length === 0) {
    return (
      <div className="empty-state" data-testid="lead360-empty">
        {EMPTY_LABEL}
      </div>
    );
  }
  return (
    <div className="lead360-records" role="list" aria-label="Lead 360 records">
      {records.map((r) => (
        <LeadCard key={r.safeLeadId} input={r} />
      ))}
    </div>
  );
}

/** Default number of rows shown in the compact dashboard preview. */
const PREVIEW_DEFAULT_LIMIT = 3;

export interface Lead360PreviewContentProps {
  records: readonly Lead360Input[];
  /** Selected clinic id — the row-link fallback when a record's own clinic is null. */
  clinicId?: string | number | null;
  /** Max rows to preview (default 3). */
  limit?: number;
}

/**
 * Compact "latest leads" preview for the Owner dashboard (VISUAL-POLISH-01). Shows
 * up to `limit` one-line rows (safe ref, compact source, SHORT latest activity, and
 * the created date in IST), each linking into the Leads CRM object, plus a view-all
 * link — INSTEAD of the full wall of expanded record cards. Pure (no fetch); the
 * self-fetching wrapper supplies the records + selected clinic. Carries the same
 * honesty rules as the list: latest activity is the event stream only (never
 * linkage), so a row never reads as "not called", and the IST formatter keeps raw
 * ISO timestamps / microseconds out of the rendered output.
 */
export function Lead360PreviewContent({
  records,
  clinicId = null,
  limit = PREVIEW_DEFAULT_LIMIT,
}: Lead360PreviewContentProps): JSX.Element {
  const fallbackClinicId =
    clinicId === null || clinicId === '' ? null : String(clinicId);
  const shown = records.slice(0, Math.max(0, limit));

  if (shown.length === 0) {
    return (
      <div className="empty-state" data-testid="lead360-preview-empty">
        No leads to preview for this clinic yet.
      </div>
    );
  }

  return (
    <div data-testid="lead360-preview">
      <ul className="lead360-preview" aria-label="Latest leads">
        {shown.map((r) => {
          const view = buildLead360View(r);
          const linkClinicId = r.clinicId ?? fallbackClinicId;
          return (
            <li
              className="lead360-preview__row"
              key={view.safeLeadId}
              data-testid="lead360-preview-row"
            >
              <Link
                to={leadDetailHref(view.safeLeadId, linkClinicId)}
                className="lead360-preview__ref text-primary underline-offset-4 hover:underline"
              >
                {formatLeadRef(view.safeLeadId)}
              </Link>
              <span className="lead360-preview__source">
                {compactSourceSummary(r)}
              </span>
              <span className="lead360-preview__activity">
                {latestActivitySummaryShort(view)}
              </span>
              <span className="lead360-preview__when">
                {formatTimestampIst(view.createdAtDisplay)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="lead360-preview__more">
        <Link
          to="/dashboard/owner/leads"
          className="text-primary underline-offset-4 hover:underline"
        >
          View all leads →
        </Link>
      </p>
    </div>
  );
}

export interface Lead360ClinicOption {
  clinicId: string;
  clinicName: string;
}

export interface Lead360PanelProps {
  /**
   * Safe fetcher for `GET /api/owner/lead-360` (`fetchOwnerLead360`). Injected so
   * the fetch path stays unit-testable. MUST be a stable (module-level)
   * reference — the effect re-runs when its identity changes.
   */
  fetcher: (clinicId: string | number, limit?: number) => Promise<Lead360Input[]>;
  /** Clinics already loaded on the Owner dashboard (id + label). No PHI. */
  clinics: readonly Lead360ClinicOption[];
  limit?: number;
  title?: string;
  /**
   * Render the compact "latest leads" PREVIEW (a few one-line rows + a view-all
   * link) instead of the full wall of expanded record cards. Used on the Owner
   * dashboard (VISUAL-POLISH-01) so the dashboard guides to the Leads CRM object.
   */
  preview?: boolean;
  /** Max rows when `preview` is set (default 3). */
  previewLimit?: number;
}

/**
 * Self-fetching, fault-isolated wrapper. Scopes to ONE clinic chosen from the
 * dashboard's existing clinic list (no invented id, no all-clinic listing). A
 * fetch failure renders a small note and never breaks sibling panels.
 */
export function Lead360Panel({
  fetcher,
  clinics,
  limit,
  title = DEFAULT_TITLE,
  preview = false,
  previewLimit,
}: Lead360PanelProps): JSX.Element {
  const valid = clinics.filter((c) => c.clinicId.trim() !== '');
  const [selectedClinicId, setSelectedClinicId] = useState<string>(
    valid[0]?.clinicId ?? '',
  );
  const [data, setData] = useState<Lead360Input[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedClinicId === '') return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetcher(selectedClinicId, limit)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load Lead 360');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, selectedClinicId, limit]);

  // No clinic to scope to — render a clear, non-blank state (never a broken panel).
  if (valid.length === 0) {
    return (
      <section className="clinic-table" aria-label="Lead 360">
        <h2 className="clinic-table__title">{title}</h2>
        <div className="empty-state" data-testid="lead360-no-clinic">
          No clinic is available to scope Lead 360 yet.
        </div>
      </section>
    );
  }

  return (
    <section className="clinic-table" aria-label="Lead 360">
      <h2 className="clinic-table__title">{title}</h2>
      {/*
        OWNER-TRUTH-FIXES-V1 item 2 (SCREEN-TENANCY-AUDIT-V1 CANON-GAP 1 /
        A7 top-10 #9 -- STALE SECURITY-CLAIM COPY): this subtitle claimed a
        "PHI-free lead record". Since the CONTACT-REVEAL-AUDIT-01 carve-out
        that is no longer true of the surface -- the record shows the lead
        name to scoped staff and hosts the audited phone reveal, as this
        file's own LEAD_RECORD_DATA_NOTE_V2 already documents. The claim is
        replaced with a plain description of what the surface actually
        shows, reusing that constant's established accurate wording (one
        vocabulary, never a second). Describes the surface; claims no
        certification or compliance status.
      */}
      <p
        className="clinic-table__subtitle"
        data-testid="lead360-panel-subtitle"
      >
        Scoped lead record for one clinic: the lead name is visible to staff
        with verified clinic access, and the phone only through the audited
        reveal (every reveal is logged). Activity (what happened to the lead)
        is shown separately from integration linkage — Office24x7 telephony,
        walk-in, and revenue — so a “not linked yet” linkage never reads as
        “not contacted”.
      </p>

      {valid.length > 1 ? (
        <p className="clinic-table__subtitle">
          <label htmlFor="lead360-clinic-select">Clinic: </label>
          <select
            id="lead360-clinic-select"
            data-testid="lead360-clinic-select"
            value={selectedClinicId}
            onChange={(e) => setSelectedClinicId(e.target.value)}
          >
            {valid.map((c) => (
              <option key={c.clinicId} value={c.clinicId}>
                {c.clinicName}
              </option>
            ))}
          </select>
        </p>
      ) : (
        <p className="clinic-table__subtitle">Clinic: {valid[0]?.clinicName}</p>
      )}

      {loading ? (
        <div className="loading">Loading…</div>
      ) : error !== null ? (
        <p data-testid="lead360-error">Lead 360 unavailable. {error}</p>
      ) : preview ? (
        <Lead360PreviewContent
          records={data ?? []}
          clinicId={selectedClinicId}
          limit={previewLimit}
        />
      ) : (
        <Lead360Content records={data ?? []} />
      )}
    </section>
  );
}
